import React, { useState, useEffect } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { CreditCard, Plus, ChevronDown } from "lucide-react";
import QuickPaymentPanel from "../QuickPaymentPanel";
import PosPaymentStep from "../PosPaymentStep";
import { useAuth } from "../../auth/AuthContext";
import { getSettings } from "../../services/settings";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";

import AddSaleDropdown from "./AddSaleDropdown";

interface DashboardHeaderProps {
  onQuickAction: (action: string) => void;
  onDateChange: (startDate: string, endDate: string) => void;
}

const DashboardHeader = ({
  onQuickAction,
  onDateChange,
  currencySymbol = "£",
}: DashboardHeaderProps & { currencySymbol?: string }) => {
  const { user } = useAuth();
  const [showQuickPaymentPanel, setShowQuickPaymentPanel] = useState(false);
  const [sidebarMode, setSidebarMode] = useState<"quick" | "paynow" | null>(
    null
  );
  const [quickPaymentType, setQuickPaymentType] = useState<"direct" | "stylist">("stylist");

  // Fetch quick payment type from settings
  useEffect(() => {
    const fetchQuickPaymentType = async () => {
      try {
        const response = await getSettings();
        if (response.success && response.data?.settings) {
          const paymentType = response.data.settings.quick_payment_type;
          // quick_payment_type can be "stylist", "direct", "active" (for stylist), or "inactive" (for direct)
          // Backend stores "active" for stylist mode and "inactive" for direct mode
          if (paymentType === "stylist" || paymentType === "active") {
            setQuickPaymentType("stylist");
          } else if (paymentType === "direct" || paymentType === "inactive") {
            setQuickPaymentType("direct");
          } else {
            // Default fallback to stylist
            setQuickPaymentType("stylist");
          }
        }
      } catch (error) {
        console.error("Failed to fetch quick payment type:", error);
        // Fallback to stylist if settings fetch fails
        setQuickPaymentType("stylist");
      }
    };

    fetchQuickPaymentType();
  }, []);

  const handleRangeChange = (value: string) => {
    const endDate = new Date();
    let startDate = new Date();

    switch (value) {
      case "This Month":
        startDate.setMonth(endDate.getMonth());
        startDate.setDate(1);
        break;
      case "6 Months":
        startDate.setMonth(endDate.getMonth() - 6);
        break;
      case "1 Year":
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
      default:
        startDate.setMonth(endDate.getMonth() - 6);
    }

    const startStr = startDate.toISOString().split("T")[0];
    const endStr = endDate.toISOString().split("T")[0];

    onDateChange(startStr, endStr);
  };

  const handleAddSaleAction = (type: "Services" | "Sunbeds") => {
    if (type === "Services") {
      onQuickAction("Add Sale");
    } else if (type === "Sunbeds") {
      onQuickAction("Add Sunbed Sale");
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 py-2 sm:py-0">
      <div className="flex items-center justify-between w-full lg:w-auto gap-2">
        <h1 className="text-[18px] sm:text-2xl md:text-3xl font-bold text-[#402B69] whitespace-nowrap">
          Dashboard Overview
        </h1>

        {/* Radix Select */}
        <Select onValueChange={handleRangeChange} defaultValue="This Month">
          <SelectTrigger className="w-[120px] sm:w-40 bg-white shadow-sm border-gray-100 focus:ring-[#885ABB]/20 h-9 sm:h-10 text-xs sm:text-sm">
            <SelectValue placeholder="Select Range" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="This Month">This Month</SelectItem>
            <SelectItem value="6 Months">6 Months</SelectItem>
            <SelectItem value="1 Year">1 Year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 min-w-fit">
        <button
          onClick={() => setShowQuickPaymentPanel(true)}
          className="whitespace-nowrap bg-[#22C55E] hover:bg-[#16a34a] text-white rounded-lg py-3 px-4 sm:px-6 font-bold flex items-center justify-center gap-2 transition shadow-sm text-xs sm:text-sm"
        >
          <img src="/quick.svg" alt="Quick Payment" className="w-4 h-4 sm:w-5 sm:h-5" />
          <span>Quick Payment</span>
        </button>

        <AddSaleDropdown onAction={handleAddSaleAction} />
      </div>


      {showQuickPaymentPanel && (
        <QuickPaymentPanel
          onClose={() => setShowQuickPaymentPanel(false)}
          onProceed={() => {
            setShowQuickPaymentPanel(false);
            setSidebarMode("quick");
          }}
          quickPaymentType={quickPaymentType}
        />
      )}

      {sidebarMode && (
        <PosPaymentStep
          selectedService={{
            id: "service-1",
            name: "Hair Cut",
            duration: "60 mins",
            price: `${currencySymbol}35`,
          }}
          selectedCustomer="Karen Davies"
          appointmentTime="9.30–10.30 am"
          tip={0}
          setTip={() => { }}
          goToFinalPayment={() => setSidebarMode(null)}
          onClose={() => setSidebarMode(null)}
          mode={sidebarMode}
        />
      )}
    </div>
  );
};

export default DashboardHeader;
